import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingBag, User, Menu, X, Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { money } from "@/lib/format";

const NAV_LINKS = [
  { label: "New Arrivals", to: "/shop?sort=newest" },
  { label: "Shop All", to: "/shop" },
  { label: "Brands", to: "/brands" },
  { label: "Men", to: `/shop?gender=${encodeURIComponent("Men's")}` },
  { label: "Women", to: `/shop?gender=${encodeURIComponent("Women's")}` },
  { label: "Sell", to: "/sell" },
  { label: "Our Story", to: "/about" },
];

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export function Header() {
  const { user } = useAuth();
  const { items } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return;
    }
    api.get("/account/notifications").then((res) => setUnreadCount(res.data.unread_count)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!q || q.length < 2) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(() => {
      api.get(`/search`, { params: { q } }).then((res) => setSuggestions(res.data.slice(0, 6))).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/shop?search=${encodeURIComponent(q)}`);
    setSearchOpen(false);
    setQ("");
  };

  return (
    <div data-testid="site-header">
      <div className="bg-[#0A0A0C] text-white text-[11px] tracking-[0.15em] py-2.5 px-4 text-center font-body uppercase" data-testid="announcement-bar">
        Free Shipping Australia-wide with orders <span className="text-teal-400">$200 and above</span>
      </div>
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-neutral-200/80">
        <div className="container-lookers">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center h-20">
            <div className="flex items-center justify-self-start">
              <button className="lg:hidden w-10 h-10 flex items-center justify-center text-charcoal hover:text-teal-600 transition-colors" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button" aria-label="Open menu">
                <Menu size={22} />
              </button>
              <form onSubmit={submitSearch} className="hidden lg:flex items-center gap-2 h-10 px-4 rounded-full border border-neutral-200 w-44 text-charcoal focus-within:border-teal-600 transition-colors" data-testid="header-search-form">
                <Search size={16} className="text-neutral-400 shrink-0" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search"
                  aria-label="Search frames"
                  className="bg-transparent outline-none text-sm w-full placeholder:text-neutral-400 font-body"
                  data-testid="header-search-field"
                />
              </form>
            </div>

            <Link to="/" className="flex items-center justify-center justify-self-center" data-testid="logo-link">
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/lookers/branding/logo_black.png`} alt="Lookers" className="h-11 w-auto object-contain" />
            </Link>

            <div className="flex items-center gap-0.5 justify-self-end">
              <button onClick={() => setSearchOpen(!searchOpen)} aria-label="Search" data-testid="search-toggle-button-mobile" className="lg:hidden w-10 h-10 rounded-full flex items-center justify-center text-charcoal hover:bg-neutral-100 hover:text-teal-600 transition-colors">
                <Search size={18} />
              </button>
              <Link to={user ? "/account/wishlist" : "/login"} aria-label="Wishlist" data-testid="wishlist-link" className="w-10 h-10 rounded-full flex items-center justify-center text-charcoal hover:bg-neutral-100 hover:text-teal-600 transition-colors">
                <Heart size={18} />
              </Link>
              {user && (
                <Link to="/account/notifications" aria-label="Notifications" data-testid="notifications-link" className="relative w-10 h-10 rounded-full hidden sm:flex items-center justify-center text-charcoal hover:bg-neutral-100 hover:text-teal-600 transition-colors">
                  <Bell size={18} />
                  {unreadCount > 0 && (
                    <span className="absolute top-1.5 right-1 bg-teal-600 text-white text-[10px] font-heading font-bold w-4 h-4 rounded-full flex items-center justify-center" data-testid="notifications-unread-count">
                      {unreadCount}
                    </span>
                  )}
                </Link>
              )}
              <Link to={user ? "/account" : "/login"} aria-label="Account" data-testid="account-link" className="w-10 h-10 rounded-full hidden sm:flex items-center justify-center text-charcoal hover:bg-neutral-100 hover:text-teal-600 transition-colors">
                <User size={18} />
              </Link>
              <Link to="/cart" aria-label="Cart" className="relative w-10 h-10 rounded-full flex items-center justify-center text-charcoal hover:bg-neutral-100 hover:text-teal-600 transition-colors" data-testid="cart-link">
                <ShoppingBag size={18} />
                {items.length > 0 && (
                  <span className="absolute top-1.5 right-1 bg-teal-600 text-white text-[10px] font-heading font-bold w-4 h-4 rounded-full flex items-center justify-center" data-testid="cart-count">
                    {items.length}
                  </span>
                )}
              </Link>
            </div>
          </div>

          <nav className="hidden lg:flex items-center justify-center gap-1 h-12 border-t border-neutral-100 font-heading font-bold text-[13px] uppercase tracking-tight text-charcoal">
            {NAV_LINKS.map((l) => (
              <Link key={l.label} to={l.to} className="px-4 py-2 rounded-full hover:bg-neutral-100 hover:text-teal-600 transition-colors" data-testid={`nav-${slugify(l.label)}`}>
                {l.label}
              </Link>
            ))}
          </nav>
        </div>

        {searchOpen && (
          <div className="border-t border-neutral-100 bg-white">
            <form onSubmit={submitSearch} className="container-lookers py-4">
              <div className="relative">
                <div className="flex items-center border-b-2 border-charcoal focus-within:border-teal-600 transition-colors">
                  <Search size={16} className="text-zinc-400 shrink-0" />
                  <input
                    autoFocus
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search “black Ray-Bans”, “round glasses”, “under $150”…"
                    className="w-full pl-3 py-2.5 text-sm font-body outline-none bg-transparent text-charcoal placeholder:text-zinc-400"
                    data-testid="search-input"
                  />
                </div>
                {suggestions.length > 0 && (
                  <div className="absolute left-0 right-0 top-full bg-white border border-neutral-200 rounded-2xl shadow-lg shadow-neutral-900/5 mt-2 overflow-hidden z-10">
                    {suggestions.map((p) => (
                      <Link
                        key={p.id}
                        to={`/product/${p.slug}`}
                        onClick={() => setSearchOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-neutral-50"
                        data-testid={`search-suggestion-${p.id}`}
                      >
                        <img src={p.images?.[0]?.url} alt="" className="w-12 h-12 object-contain bg-[#F8F8F9] rounded-xl p-1.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-neutral-900 truncate">{p.brand_name} {p.model}</div>
                          <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-400">{p.condition_score}/10 {p.condition_label}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-sm font-mono font-bold text-neutral-900">{money(p.selling_price)}</div>
                          {p.original_rrp && <div className="text-[10px] font-mono text-neutral-400 line-through">{money(p.original_rrp)}</div>}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </form>
          </div>
        )}
      </header>

      {mobileOpen && createPortal(
        <div className="fixed inset-0 z-50 bg-white lg:hidden" data-testid="mobile-menu-panel">
          <div className="flex justify-between items-center p-6 border-b border-neutral-100">
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/lookers/branding/logo_black.png`} alt="Lookers" className="h-6 w-auto object-contain" />
            <button onClick={() => setMobileOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close" className="text-charcoal">
              <X size={22} />
            </button>
          </div>
          <nav className="flex flex-col p-6 gap-6 font-heading font-semibold text-2xl uppercase tracking-tight text-charcoal">
            {NAV_LINKS.map((l) => (
              <Link key={l.label} to={l.to} onClick={() => setMobileOpen(false)} data-testid={`mobile-nav-${slugify(l.label)}`}>
                {l.label}
              </Link>
            ))}
          </nav>
        </div>,
        document.body
      )}
    </div>
  );
}
